// RateHawk (ETG – Emerging Travel Group) API types used in the frontend.
//
// Architecture overview:
//  - SERP search returns only hotel IDs + rates — NO photos/name/description.
//  - Hotel content (photos, name, address, star_rating, etc.) is fetched
//    separately from the Content API and merged in the edge function.
//  - Rooms are NOT first-class objects: each Rate owns a room_name.
//    The UI groups rates by room_name into RateHawkRoomGroup objects.
//  - Booking requires a book_hash (only available from the Hotelpage call,
//    not from the SERP call). The edge function steps are therefore:
//      1. stays-search  → SERP + Content enrichment → RateHawkSearchResult[]
//      2. stays-rates   → Hotelpage (/search/hp/)   → RateHawkRate[] with book_hash

// ─── Search Request ──────────────────────────────────────────────────────────

/** One room's guest configuration (adults + children ages). */
export interface RateHawkRoomGuest {
  adults: number;
  /** Ages of children (0–17). Empty array means no children. */
  children: number[];
}

/**
 * UI-level per-room guest configuration, edited independently per room
 * before being converted into RateHawkRoomGuest[] for the API request.
 */
export interface RateHawkRoomGuestConfig {
  adults: number;
  /** Ages of each child in this room (0–17). */
  childrenAges: number[];
}

/** Server-side filters applied to the cached search results. */
export interface RateHawkSearchFilter {
  /**
   * Minimum star rating (inclusive). E.g., 4 → only 4+ star hotels.
   * 1 e 2 stelle non sono praticamente mai in catalogo, ma la casella resta
   * selezionabile: filtra semplicemente su zero risultati anziché sparire.
   */
  min_star_rating?: 1 | 2 | 3 | 4 | 5;
  /**
   * Se includere gli hotel con star_rating 0. In ETG lo 0 non significa
   * "scadente" ma "non classificato": appartamenti, ville, cottage e boutique,
   * il 39,6% del catalogo. Default true — vanno esclusi solo su richiesta
   * esplicita, e conta solo quando min_star_rating è impostato.
   */
  include_unrated?: boolean;
  /** Hotel types to include. Empty = all. E.g. ["Hotel", "Apartment"]. */
  kind?: string[];
  /** Board types — at least one rate must match. E.g. ["breakfast", "all-inclusive"]. */
  meal_type?: string[];
  /** Min total stay price based on cheapest rate. */
  price_from?: number;
  /** Max total stay price based on cheapest rate. */
  price_to?: number;
  /**
   * Ricerca sul nome struttura, case-insensitive e su match parziale.
   * Era un filtro client-only: con la paginazione server-side deve stare qui,
   * altrimenti cercherebbe solo dentro la pagina visualizzata.
   */
  name?: string;
  /** Amenity richieste: la struttura deve averle TUTTE (AND, non OR). */
  amenities?: string[];
}

/** Campi su cui la edge function sa ordinare. */
export type RateHawkSortField = "price" | "rating" | "name" | "distance";
export type RateHawkSortDir = "asc" | "desc";

/**
 * Ordinamento server-side. Sta a BE insieme alla paginazione: ordinare a FE la
 * sola pagina ricevuta darebbe un ordine diverso pagina per pagina.
 */
export interface RateHawkSearchSort {
  field: RateHawkSortField;
  dir: RateHawkSortDir;
}

/** Valori ammessi per la dimensione pagina. */
export const RATEHAWK_PAGE_SIZES = [25, 50, 100] as const;
export type RateHawkPageSize = (typeof RATEHAWK_PAGE_SIZES)[number];

/**
 * Valori selezionabili nei filtri, calcolati dal server sull'intero set di
 * risultati della ricerca. Non vanno derivati dai risultati ricevuti: quelli
 * sono una pagina sola, e le caselle disponibili cambierebbero girando pagina.
 */
export interface RateHawkSearchFacets {
  kinds: string[];
  amenities: string[];
}

/** Parameters for the geo-based accommodation search. */
/**
 * Modalità di ricerca alloggi.
 *  - "region": destinazione scelta dall'autocomplete → ETG /serp/region/
 *  - "geo":    punto scelto a mano sulla mappa       → ETG /serp/geo/ + radius
 * Il client la dichiara esplicitamente; la edge function la valida e la applica.
 */
export type RateHawkSearchMode = "region" | "geo";

export interface RateHawkSearchParams {
  checkin: string;     // ISO date YYYY-MM-DD
  checkout: string;    // ISO date YYYY-MM-DD
  /** One entry per room requested. */
  guests: RateHawkRoomGuest[];
  latitude: number;
  longitude: number;
  /** Search radius in meters (1 – 70 000). Usato solo se la ricerca ricade sul ramo geo. */
  radius: number;
  /**
   * Modalità scelta dall'utente. "region" = destinazione dall'autocomplete,
   * "geo" = punto scelto a mano sulla mappa. Determina quali campi sono
   * significativi: region_id per la prima, latitude/longitude/radius per la
   * seconda. Omessa = "geo" (comportamento storico).
   */
  search_mode?: RateHawkSearchMode;
  /** ETG region id. Obbligatorio quando `search_mode` è "region". */
  region_id?: number;
  /** ISO 4217 currency code. Defaults to EUR. */
  currency?: string;
  /** ISO 3166-1 alpha-2 traveller residency code. Required by RateHawk. */
  residency?: string;
  /** Server-side filters applied on cached results. */
  filter?: RateHawkSearchFilter;
  /** Ordinamento server-side. Omesso = prezzo crescente. */
  sort?: RateHawkSearchSort;
  /** Pagina 1-based. Omessa = 1. */
  page?: number;
  /** Risultati per pagina (25/50/100). Omessa = 25. */
  page_size?: RateHawkPageSize;
  /**
   * Chiave della ricerca già in cache, restituita dalla risposta precedente.
   * È un fast-path: la si rimanda quando cambiano solo filtri, ordinamento o
   * pagina, così il server salta la ricostruzione della chiave. Se la entry è
   * scaduta il server rifà comunque la ricerca — non è un impegno vincolante.
   */
  cache_key?: string;
}

// ─── Rate & Pricing ──────────────────────────────────────────────────────────

/** Commission breakdown (show = display currency, charge = booking currency). */
export interface RateHawkCommissionInfo {
  show: {
    amount_gross: string;
    amount_net: string;
    amount_commission: string;
  };
  charge: {
    amount_gross: string;
    amount_net: string;
    amount_commission: string;
  };
}

/**
 * Single cancellation policy window.
 * Penalty = amount_show when cancelled between start_at and end_at.
 * null means start/end is open-ended.
 */
export interface RateHawkCancellationPolicy {
  start_at: string | null;   // ISO 8601 datetime
  end_at: string | null;     // ISO 8601 datetime
  /** Cancellation charge in display currency. "0.00" = free in this window. */
  amount_show: string;
  /** Cancellation charge in booking currency. */
  amount_charge: string;
  commission_info: RateHawkCommissionInfo;
}

/** A payment option attached to a rate. */
export interface RateHawkPaymentType {
  /** Total charge in booking currency (may differ from show currency). */
  amount: string;
  /** Total charge in display/show currency. Use this for display. */
  show_amount: string;
  /** Booking currency code. */
  currency_code: string;
  /** Display/show currency code. */
  show_currency_code: string;
  /** "deposit" | "now" | "hotel" etc. */
  type: string;
  cancellation_penalties: {
    policies: RateHawkCancellationPolicy[];
    /**
     * ISO 8601 datetime before which cancellation is FREE.
     * null means non-refundable (no free window).
     */
    free_cancellation_before: string | null;
  };
  commission_info: RateHawkCommissionInfo;
  /**
   * Tax breakdown (city tax, occupancy tax, etc.). RateHawk nests this here
   * on Hotelpage responses rather than at the top level of the rate — see
   * resolveTaxData() in ratehawk-accommodation/helpers.ts.
   */
  tax_data?: { taxes: Array<{ name: string; amount: string; currency_code: string; included_by_supplier: boolean }> } | null;
}

/** Encoding of a room's physical attributes. All are numeric codes. */
export interface RateHawkRgExt {
  class: number;
  quality: number;
  sex: number;
  bathroom: number;
  bedding: number;
  family: number;
  capacity: number;
  club: number;
  bedrooms: number;
  balcony: number;
  view: number;
  floor: number;
}

/** Translated/parsed room attributes. */
export interface RateHawkRoomDataTrans {
  main_room_type: string;
  main_name: string;
  bathroom: string | null;
  bedding_type: string | null;
  misc_room_type: string | null;
  beds: { bed: string; count: number }[];
}

/**
 * A single rate returned by either the SERP or Hotelpage endpoint.
 *
 * SERP rates do NOT include book_hash (can't be booked directly).
 * Hotelpage rates include book_hash (required for booking).
 *
 * Note on pricing: the API returns show_amount/show_currency_code at the
 * top level of the rate (primary source per ETG docs) AND inside
 * payment_options.payment_types[0]. Always prefer the top-level fields
 * when available.
 */
export interface RateHawkRate {
  /** Stable identifier for comparing the same room across multiple searches. */
  match_hash: string;
  /** Search-scoped identifier (required for prebook from search step). */
  search_hash: string;
  /**
   * Booking token. Present ONLY in Hotelpage responses.
   * Required by "Prebook rate from hotelpage step".
   */
  book_hash?: string;
  /**
   * Total price in display/show currency — top-level field returned by SERP.
   * Prefer this over payment_options.payment_types[0].show_amount.
   */
  show_amount?: string;
  /** Display/show currency code — top-level field returned by SERP. */
  show_currency_code?: string;
  /**
   * Price per night in show_currency_code.
   * Array length = number of nights.
   */
  daily_prices: string[];
  /**
   * Board / meal plan identifier.
   * Values: "nomeal" | "breakfast" | "halfboard" | "fullboard" |
   *         "all-inclusive" | "soft-all-inclusive" | "ultra-all-inclusive"
   *         | "super-all-inclusive" | "dinner" | "lunch" | "breakfast_for_one"
   */
  meal: string;
  meal_data: {
    value: string;
    has_breakfast: boolean;
    no_child_meal: boolean;
  };
  payment_options: {
    payment_types: RateHawkPaymentType[];
  };
  rg_ext: RateHawkRgExt;
  /** Human-readable room name (e.g. "Deluxe Double room (full double bed)"). */
  room_name: string;
  room_name_info: {
    /** Supplier's original room name. */
    original_rate_name: string;
  };
  /** Room amenity slugs (e.g. "king-bed", "non-smoking", "sea-view"). */
  amenities_data: string[];
  /** Number of rooms remaining with this rate. */
  allotment: number;
  room_data_trans: RateHawkRoomDataTrans;
  any_residency: boolean;
  is_package: boolean;
  /** No-show penalty. amount "0.00" means no penalty. */
  no_show?: { amount: string; currency_code: string; from_time: string } | null;
  /** Deposit details (present when payment type is "hotel"). */
  deposit?: { amount: string; currency_code: string; is_refundable: boolean } | null;
  /** Tax breakdown (city tax, occupancy tax, etc.). */
  tax_data?: { taxes: Array<{ name: string; amount: string; currency_code: string; included_by_supplier: boolean }> } | null;
  /** VAT information. */
  vat_data?: { amount: string; currency_code: string; included: boolean; applied: boolean } | null;
  /** Minimum/maximum resale price constraints. */
  sell_price_limits?: { min_price?: string; max_price?: string } | null;
  /** Best Available Rate comparison data (requires special API access). */
  bar_rate_price_data?: { amount: string; currency_code: string } | null;
  /** Amenity filter tags from SERP (e.g. "has_bathroom", "has_balcony"). */
  serp_filters?: string[];
  /** Upsell services (early check-in / late checkout options). */
  perks?: {
    early_checkin?: Array<{ time: string; is_requested: boolean }>;
    late_checkout?: Array<{ time: string; is_requested: boolean }>;
  } | null;
  /** Legal/regulatory info (country-specific, e.g. Russia). */
  legal_info?: unknown | null;
  /**
   * Photo URLs for this room type, populated by stays-rates-rh matching rg_ext
   * against ratehawk_static_hotels.room_groups. Empty when no static match found.
   */
  room_photos?: string[];
}

// ─── Hotel Content (from Content API) ───────────────────────────────────────

export interface RateHawkHotelImage {
  /** Full image URL. */
  url: string;
  caption?: string;
  /** Tag/category of the photo (e.g. "exterior", "room"). */
  tag?: string;
  width?: string;
  height?: string;
}

/** Hotel metadata fetched from the Content API (not in SERP results). */
export interface RateHawkHotelContent {
  hid: number;
  id: string;
  name: string;
  /** Full street address. */
  address: string;
  latitude: number;
  longitude: number;
  /** Star rating (integer 1–5). */
  star_rating: number;
  /** "HH:MM:SS" e.g. "14:00:00". */
  check_in_time: string;
  /** "HH:MM:SS" e.g. "12:00:00". */
  check_out_time: string;
  /** Structured description sections. */
  description_struct: { title: string; paragraphs: string[] }[];
  images_ext: RateHawkHotelImage[];
  amenity_groups: { group_name: string; amenities: string[] }[];
  region: {
    id: number;
    name: string;
    country_code: string;
    type: string;
  };
  /** Hotel type: "Hotel" | "Hostel" | "Apartment" | etc. */
  kind: string;
}

// ─── Enriched Search Result ──────────────────────────────────────────────────

/**
 * A hotel as it arrives on the frontend after the stays-search edge function
 * has merged SERP data with Content API data.
 *
 * Enriched hotel result returned by the stays-search edge function.
 */
export interface RateHawkSearchResult {
  /** Hotel slug identifier, e.g. "royal_central_hotel_the_palm". */
  id: string;
  /** Numeric hotel ID used in Content API and Hotelpage calls. */
  hid: number;
  name: string;
  address: string;
  /** City / region name from Content API region. */
  cityName: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  /** Integer 1–5 (or 0 if unknown). */
  starRating: number;
  /** Hotel type from Content API. E.g. "Hotel" | "Hostel" | "Apartment" | "Villa". */
  kind: string;
  /** First photography URL, or empty string. */
  coverPhoto: string;
  /**
   * Gallery completa. Vuota nei risultati di ricerca (la lista usa solo
   * coverPhoto): viene popolata da fetchRates() sull'hotel selezionato.
   */
  photos: string[];
  /**
   * Estratto (primo paragrafo, max 400 caratteri) dalla ricerca; sostituito
   * dalla descrizione completa una volta risolta fetchRates().
   */
  description: string;
  /** Amenity slugs from Content API amenity_groups. */
  amenities: string[];
  /** check_in_time trimmed to "HH:MM" e.g. "14:00". */
  checkInTime: string;
  /** check_out_time trimmed to "HH:MM" e.g. "12:00". */
  checkOutTime: string;
  /**
   * The cheapest rate from this hotel's SERP result set.
   * Use cheapestShowAmount / cheapestShowCurrency for display.
   */
  cheapestRate: RateHawkRate | null;
  cheapestShowAmount: string;
  cheapestShowCurrency: string;
  /**
   * Distinct meal types available across this hotel's SERP rates, for the
   * meal_type filter. L'array rates completo non viene più trasferito: pesava
   * ~18 kB per hotel e ETG sconsiglia di far selezionare una tariffa dalla
   * SERP — la selezione passa sempre da fetchRates() (Hotelpage).
   */
  meals: string[];
  /**
   * Raw ETG metapolicy_struct from the static hotel dump (e.g. pets, deposit,
   * no_show, check_in_check_out categories). Shape varies per hotel/category;
   * only populated once fetchRates() has resolved for this hotel. Null if not
   * yet available or not synced by the static data pipeline.
   */
  metapolicy_struct?: Record<string, unknown> | null;
  /** Free-text policy notes from ETG (may contain inline <a> HTML tags). */
  metapolicy_extra_info?: string | null;
}

// ─── Room Group (UI concept) ──────────────────────────────────────────────────

/**
 * Rates grouped by rg_ext for display in the RatesView.
 * Constructed on the fly from Hotelpage rates; rates with the same rg_ext are merged
 * into one group. Falls back to grouping by room_name when rg_ext is absent.
 */
export interface RateHawkRoomGroup {
  /** Display name for this room type, taken from the cheapest rate's room_name. */
  roomName: string;
  /** e.g. "1x double, 2x single". */
  bedsLabel: string;
  /** Amenity slugs taken from the first rate's amenities_data. */
  amenities: string[];
  /**
   * Photo URLs for this room type (WorldOTA CDN, contain {size} placeholder).
   * Populated by stays-rates-rh matching rate.rg_ext against ratehawk_static_hotels.room_groups.
   * Empty when no static match is found.
   */
  photos: string[];
  /** All rates for this room type, sorted by show_amount ascending. */
  rates: RateHawkRate[];
}

// ─── Hotelpage Response ───────────────────────────────────────────────────────

/** Response shape from the stays-rates edge function (wraps Hotelpage). */
export interface RateHawkHotelPageResponse {
  hotels: Array<{
    id: string;
    hid: number;
    /** All available rates, each including book_hash. */
    rates: RateHawkRate[];
  }>;
}

// ─── Autocomplete ─────────────────────────────────────────────────────────────

export interface RateHawkAutocompleteRegion {
  id: number;
  name: string;
  /** e.g. "City" | "Country" | "Airport" | "Multi-Region (within a country)". */
  type: string;
  country_code: string;
}

export interface RateHawkAutocompleteHotel {
  id: string;
  hid: number;
  name: string;
  region_id: number;
}

export interface RateHawkAutocompleteResponse {
  hotels: RateHawkAutocompleteHotel[];
  regions: RateHawkAutocompleteRegion[];
}
